import {
  Flex,
  useDisclosure,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverTrigger,
  PopoverContent,
} from "@chakra-ui/react";
import { HorizontalGreyBlackField, HorizontalGreyBlackFieldInput } from "components/DataDisplay/GreyBlackField";
import { UseFormReturn, useController } from "react-hook-form";
import { longAttributeStyles } from "components/DataDisplay/styles";
import { PlusCirlceButtonWithRef } from "components/Button/PlusCircleButton";
import RecordTargetTag from "features/RecordTarget/RecordTargetTag";
import { RecordTarget, TargetType } from "features/RecordTarget/models";
import RecordTargetForm from "features/Resources/Shared/Components/Form/RecordTargetForm";
import { useCallback } from "react";
import { IconType } from "react-icons";
import { Schema } from "../Form/RecordTargetForm/schema";

interface RecordTargetFieldProps {
  form: UseFormReturn<any>;
  label: string;
  fieldName: string;
  recordTargetTypes: TargetType[] | "All";
  defaultTargetType?: TargetType;
  icon?: IconType;
  includeAdd?: boolean;
  disabled?: boolean;
}

export default function RecordTargetField({
  form,
  fieldName,
  label,
  icon,
  defaultTargetType,
  recordTargetTypes,
  disabled = false,
  includeAdd = true,
}: RecordTargetFieldProps) {
  const {
    control,
    formState: { errors },
  } = form;
  const { field } = useController({ control, name: fieldName });

  const { isOpen, onOpen, onClose, getDisclosureProps } = useDisclosure();
  const disclosureProps = getDisclosureProps();

  const onSubmit = useCallback(
    (data: Schema) => {
      form.setValue(fieldName, data, { shouldValidate: true, shouldDirty: true });
      onClose();
    },
    [fieldName, form, onClose],
  );

  return (
    <HorizontalGreyBlackFieldInput
      icon={icon}
      label={label}
      labelContainerProps={longAttributeStyles.labelContainerProps}
      name={fieldName}
      errors={errors}
    >
      <Popover matchWidth closeOnBlur={false} {...disclosureProps} onOpen={onOpen} onClose={onClose}>
        <Flex gap="1" align="center">
          {field.value && (
            <RecordTargetTag
              key={field.value.id}
              recordTarget={field.value as unknown as RecordTarget}
              remove={() => form.setValue(fieldName, null, { shouldDirty: true })} // Remove the recipient
              disabled={disabled}
            />
          )}
          {includeAdd && !disabled && !field.value && (
            <PopoverTrigger>
              <PlusCirlceButtonWithRef label={`Add ${label}`} />
            </PopoverTrigger>
          )}
        </Flex>
        <PopoverContent
          pt="3"
          bg="var(--chakra-colors-chakra-body-bg)"
          minW={{ base: "100%", lg: "max-content" }}
          width="100%"
          maxWidth="350px"
          display={isOpen ? "block" : "none"}
        >
          <PopoverCloseButton size="xs" sx={{ _hover: { bg: "transparent", color: "fg.emphasized" } }} />
          <PopoverBody>
            <RecordTargetForm
              label={label}
              onSubmit={onSubmit}
              recordTargetTypes={recordTargetTypes}
              defaultTargetType={defaultTargetType}
            />
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </HorizontalGreyBlackFieldInput>
  );
}

interface ShowRecordTargetProps {
  label: string;
  recordTarget: RecordTarget;
}

export function ShowRecordTarget({ label, recordTarget }: ShowRecordTargetProps) {
  return (
    <HorizontalGreyBlackField label={label} labelContainerProps={longAttributeStyles.labelContainerProps}>
      <RecordTargetTag recordTarget={recordTarget} disabled />
    </HorizontalGreyBlackField>
  );
}
